import { useTranslation, Trans } from 'react-i18next';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Redirect, Route, Switch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { LocaleButton } from '@gasbuddy/react-consumer-components';
import { goTo } from '../../../lib/goTo';
import ReactGTM from '../../../lib/gtm';
import useAnalyticsContext from '../../../lib/hooks/useAnalyticsContext';
import ANALYTICS_EVENTS from '../../constants/analyticsEvents';
import { LoginStatus } from '../../reducers';
import { getUserPreferences, updateUserPreferences } from '../../actions/preferences';
import ChangePassword from '../ChangePassword';
import Consent from '../Consent';
import ConstrainedRoute from '../ConstrainedRoute';
import DefaultHelmet from '../DefaultHelmet';
import Footer from '../Footer';
import Login from '../Login';
import Signup from '../Signup';
import UnknownPage from '../UnknownPage';
import styles from './Application.module.css';

const cx = classnames.bind(styles);

export default function Application({ accountId, memberName, destination, loginStatus, location }) {
  const { t, i18n } = useTranslation(['auth', 'miscellaneous']);
  const analytics = useAnalyticsContext();
  const dispatch = useDispatch();
  const { preferences, loading } = useSelector(state => state.preferences);
  const isLoggedIn = loginStatus === LoginStatus.LoggedIn;
  if (destination && isLoggedIn && typeof window !== 'undefined') {
    analytics.identifyUser({
      id: memberName,
    });
    analytics.tagEvent({ name: ANALYTICS_EVENTS.IAM_User_Successfully_Logged_In });

    ReactGTM.event('Login', {
      userID: accountId,
    });

    goTo(destination);
  }

  const search = location?.search || '';
  const pathname = location?.pathname || '/';

  // Load user preferences on mount if user is logged in
  useEffect(() => {
    /* TODO: This never triggers on the password reset page because `isLoggedIn = auth.status = 0` (logged out).
     If you set it to true somehow, then the Application component automatically redirects to the destination prop,
     because that is always populated with the default redirect.
     So, this is going to need a deep dive to figure out how to resolve this.
    */
    if (isLoggedIn && accountId && !loading && !preferences) {
      dispatch(getUserPreferences());
    }
  }, [isLoggedIn, accountId, loading, preferences, dispatch]);

  // Apply user's language preference when preferences are loaded
  useEffect(() => {
    if (preferences?.language && preferences.language !== i18n.language) {
      i18n.changeLanguage(preferences.language);
      // The language change will automatically trigger the cookie/localStorage sync via i18n event listener
    }
  }, [preferences, i18n]);

  const handleLocaleChange = useCallback((newLocale) => {
    // Update the language in i18n (this also updates localStorage via LocaleButton)
    i18n.changeLanguage(newLocale);

    // Track the locale change event
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.LOCALE_CHANGE,
      attributes: {
        newLocale,
        'Page Type': 'identity-web',
      },
    });

    // If user is logged in, update their locale preference in the database
    // TODO: See TODO in useEffect above as to why this will never trigger on the password reset page.
    if (isLoggedIn && accountId) {
      dispatch(updateUserPreferences({ language: newLocale }));
    }
  }, [i18n, analytics, isLoggedIn, accountId, dispatch]);

  return (
    <>
      <DefaultHelmet />
      <div className={cx('gradient')} />
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        zIndex: 10001,
      }}
      >
        <LocaleButton
          currentLocale={i18n.language}
          onSubmit={handleLocaleChange}
          size="sm"
          titleText={t('localeSelectTitle', { ns: 'miscellaneous' })}
          cancelText={t('actions.cancel', { ns: 'miscellaneous' })}
          submitText={t('actions.submit', { ns: 'miscellaneous' })}
        />
      </div>
      <Switch>
        <ConstrainedRoute exact path="/login/:partner?" component={Login} noindex={false}>
          <Helmet>
            <title>{t('meta.login.title', { ns: 'miscellaneous' })}</title>
            <meta property="og:title" content={t('meta.login.title', { ns: 'miscellaneous' })} />
            <meta name="twitter:title" content={t('meta.login.title', { ns: 'miscellaneous' })} />
            <meta name="description" content={t('meta.login.description', { ns: 'miscellaneous' })} />
            <meta property="og:description" content={t('meta.login.description', { ns: 'miscellaneous' })} />
            <meta name="twitter:description" content={t('meta.login.description', { ns: 'miscellaneous' })} />
            <link rel="canonical" href={`https://iam.gasbuddy.com${pathname}`} />
          </Helmet>
          <Footer>
            <Trans i18nKey="footerNewUser" ns="auth">
              New to GasBuddy?&ensp;<Link to={{ pathname: pathname.replace(/^\/login/, '/signup'), search }}>Sign Up</Link>
            </Trans>
          </Footer>
        </ConstrainedRoute>

        <ConstrainedRoute exact path="/social/login/:partner" component={Login} />

        <ConstrainedRoute exact path="/consent/:partner" component={Consent} />

        <ConstrainedRoute exact path="/signup/:partner?" component={Signup} noindex={false}>
          <Helmet>
            <title>{t('meta.signup.title', { ns: 'miscellaneous' })}</title>
            <meta property="og:title" content={t('meta.signup.title', { ns: 'miscellaneous' })} />
            <meta name="twitter:title" content={t('meta.signup.title', { ns: 'miscellaneous' })} />
            <meta name="description" content={t('meta.signup.description', { ns: 'miscellaneous' })} />
            <meta property="og:description" content={t('meta.signup.description', { ns: 'miscellaneous' })} />
            <meta name="twitter:description" content={t('meta.signup.description', { ns: 'miscellaneous' })} />
            <link rel="canonical" href={`https://iam.gasbuddy.com${pathname}`} />
          </Helmet>
          <Footer>
            <Trans i18nKey="footerExistingUser" ns="auth">
              Already a GasBuddy member?&ensp;<Link to={{ pathname: pathname.replace(/^\/signup/, '/login'), search }}>Log In</Link>
            </Trans>
          </Footer>
        </ConstrainedRoute>

        <ConstrainedRoute exact path="/password" component={ChangePassword} size="sm" />

        <ConstrainedRoute exact path="/go/:code" component={Login} />

        <ConstrainedRoute exact path="/go" component={Login} />

        <ConstrainedRoute exact path="/social/connect" component={Login} />

        <ConstrainedRoute exact path="/social/connect/:code" component={Login} />

        <Route
          exact
          path="/reset"
          render={routerProps => (
            <Redirect
              to={{
                pathname: '/password',
                search: routerProps.location.search,
              }}
            />
          )}
        />

        <Route
          exact
          path="/change"
          render={routerProps => (
            <Redirect
              to={{
                pathname: '/password',
                search: routerProps.location.search,
              }}
            />
          )}
        />

        <ConstrainedRoute component={UnknownPage} />

      </Switch>
    </>
  );
}

Application.propTypes = {
  accountId: PropTypes.number,
  memberName: PropTypes.string,
  destination: PropTypes.string,
  loginStatus: PropTypes.oneOf(Object.values(LoginStatus)),
  location: PropTypes.shape({
    search: PropTypes.string,
    pathname: PropTypes.string,
  }),
};

Application.defaultProps = {
  accountId: undefined,
  memberName: undefined,
  destination: undefined,
  loginStatus: undefined,
  location: {},
};
